import React from 'react';
import { useTranslation } from 'react-i18next';
import type { ContractReplacement, Milestone } from '../../types';
import { stageVariationMilestones } from '../../utils/stageVariation';
import { variationMoney } from './VariationPlanStep';

export function VariationChanges({
  settlement,
  milestones,
  projectAdjustmentOre = 0,
  inCard = false,
  part = 'all',
}: {
  settlement: ContractReplacement;
  milestones?: Milestone[];
  projectAdjustmentOre?: number;
  /** Inside a page card (869fa5qf8): the card carries the heading and the
   *  totals use the same figure rows as the contract summary. */
  inCard?: boolean;
  /** In the contract summary (ClickUp 869fmxfb7): the figures and the table
   *  sit apart, figures first. */
  part?: 'all' | 'table' | 'figures';
}) {
  const { t } = useTranslation();
  const scope = settlement.amendmentScope;
  if (!scope) return null;
  const rows = stageVariationMilestones(
    settlement,
    milestones ?? settlement.milestones ?? [],
  );
  const before =
    scope.originalContractAmountOre -
    scope.originalRotAmountOre +
    projectAdjustmentOre;
  const delta = settlement.afterRotDeltaOre ?? 0;
  const changes = [
    ...scope.milestones.map((source) => ({
      key: source.id,
      name:
        rows.find((row) => row.replacesMilestoneId === source.id)
          ?.description ?? source.description,
      oldName: source.description,
      before: source.amountOre - source.rotAmountOre,
      after: Math.round(
        (rows.find((row) => row.replacesMilestoneId === source.id)
          ?.afterRotAmount ?? 0) * 100,
      ),
      oldDate: source.endDate,
      newDate: rows.find((row) => row.replacesMilestoneId === source.id)
        ?.endDate,
      removed: !rows.some((row) => row.replacesMilestoneId === source.id),
    })),
    ...rows
      .filter((row) => !row.replacesMilestoneId)
      .map((row) => ({
        key: row.id,
        name: row.description,
        oldName: row.description,
        before: 0,
        after: Math.round(row.afterRotAmount * 100),
        oldDate: null,
        newDate: row.endDate,
        removed: false,
      })),
  ];
  if (part === 'figures') {
    // What the ÄTA does to the project and to the money, once each.
    return (
      <dl className="contract-summary-figures" data-testid="variation-figures">
        <div><dt>{t('variationWizard.thisChange')}</dt><dd>{variationMoney(delta)}<small className="d-block">{t('variationWizard.afterRot')}</small></dd></div>
        <div><dt>{t('variationWizard.projectAfter')}</dt><dd>{variationMoney(before + delta)}<small className="d-block">{t('variationWizard.previously', { amount: variationMoney(before) })}</small></dd></div>
        {settlement.refundAmountOre > 0
          ? <div><dt>{t('variationWizard.refundDue')}</dt><dd>{variationMoney(settlement.refundAmountOre)}</dd></div>
          : <div><dt>{t('variationWizard.newDeposit')}</dt><dd>{variationMoney(settlement.additionalPaymentOre ?? 0)}</dd></div>}
      </dl>
    );
  }
  return (
    <section
      aria-label={t('variationWizard.diffTitle')}
      data-testid="variation-changes"
    >
      {!inCard && <h3 className="fs-5">{t('variationWizard.diffTitle')}</h3>}
      <div className="table-responsive">
        <table className="table">
          <thead>
            <tr>
              <th>{t('variationWizard.stageName')}</th>
              <th>{t('variationWizard.before')}</th>
              <th>{t('variationWizard.after')}</th>
              <th>{t('variationWizard.difference')}</th>
            </tr>
          </thead>
          <tbody>
            {changes.map((change) => (
              <tr key={change.key}>
                <th scope="row">
                  {change.name}
                  {change.oldName !== change.name && (
                    <small className="d-block fw-normal">
                      {t('variationWizard.before')}: {change.oldName}
                    </small>
                  )}
                  {change.removed && (
                    <small className="d-block text-danger">
                      {t('variationWizard.omitted')}
                    </small>
                  )}
                  {(change.oldDate || null) !== (change.newDate || null) && (
                    <small className="d-block fw-normal">
                      {t('variationWizard.endDate')}:{' '}
                      {change.oldDate?.slice(0, 10) ?? '—'} →{' '}
                      {change.newDate?.slice(0, 10) ?? '—'}
                    </small>
                  )}
                </th>
                <td>{variationMoney(change.before)}</td>
                <td>{variationMoney(change.after)}</td>
                <td>{variationMoney(change.after - change.before)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!!scope.creditOffsets?.length && (
        <div className="alert alert-info">
          <h4 className="fs-6">{t('variationWizard.creditOffsetsTitle')}</h4>
          <p>{t('variationWizard.creditOffsetsHint')}</p>
          {scope.creditOffsets.map(offset => {
            const stage = (milestones ?? settlement.milestones ?? []).find(item => item.id === offset.milestoneId);
            const alreadyCommitted = stage && (stage.paidAt || stage.jointlyAttestedAt || (stage.companyAttestation === 'approved' && stage.personAttestation === 'approved'));
            return stage && <p key={offset.milestoneId} className="mb-1">{t(alreadyCommitted ? 'variationWizard.creditOffsetSettled' : 'variationWizard.creditOffset', {
              stage: stage.description, amount: variationMoney(offset.amountOre),
              remaining: variationMoney(Math.max(Math.round(stage.afterRotAmount * 100) - offset.amountOre, 0)),
            })}</p>;
          })}
        </div>
      )}
      {part === 'table' ? (
        <p className="text-muted small mb-0">{t('variationWizard.unchangedStages')}</p>
      ) : inCard ? (
        <dl className="contract-summary-figures">
          <div><dt>{t('variationWizard.thisChange')}</dt><dd>{variationMoney(delta)} {t('variationWizard.afterRot')}</dd></div>
          <div><dt>{t('variationWizard.grossChange')}</dt><dd>{variationMoney(settlement.grossDeltaOre ?? 0)}</dd></div>
          <div><dt>{t('variationWizard.rotChange')}</dt><dd>{variationMoney((settlement.grossDeltaOre ?? 0) - delta)}</dd></div>
          <div><dt>{t('variationWizard.projectAfter')}</dt><dd>{variationMoney(before)} → {variationMoney(before + delta)}</dd></div>
        </dl>
      ) : (
        <dl className="contract-wizard-summary-list">
          <dt>{t('variationWizard.thisChange')}</dt>
          <dd className="fw-bold">
            {variationMoney(delta)} {t('variationWizard.afterRot')}
          </dd>
          <dt>{t('variationWizard.grossChange')}</dt>
          <dd>{variationMoney(settlement.grossDeltaOre ?? 0)}</dd>
          <dt>{t('variationWizard.rotChange')}</dt>
          <dd>{variationMoney((settlement.grossDeltaOre ?? 0) - delta)}</dd>
          <dt>{t('variationWizard.projectAfter')}</dt>
          <dd>
            {variationMoney(before)} →{' '}
            <strong>{variationMoney(before + delta)}</strong>
          </dd>
        </dl>
      )}
    </section>
  );
}
